import {
  ArrowPathIcon,
  CalendarDaysIcon,
  MagnifyingGlassIcon,
} from "@heroicons/react/24/outline";

const fieldClass =
  "mt-1 h-10 w-full rounded-lg border border-slate-200 bg-white px-3 text-xs text-slate-700 outline-none transition focus:border-customAccentBlue focus:ring-2 focus:ring-customAccentBlue/10 dark:border-customBorder dark:bg-custom_dark_primary dark:text-customHeading";

const SelectField = ({ label, value, onChange, children }) => (
  <label className="block min-w-0 text-xs font-semibold text-customInk dark:text-customHeading">
    {label}
    <select className={fieldClass} value={value} onChange={onChange}>
      {children}
    </select>
  </label>
);

const DateField = ({ label, value, onChange }) => (
  <label className="relative min-w-0">
    <span className="sr-only">{label}</span>
    <input
      aria-label={label}
      type="date"
      value={value}
      onChange={onChange}
      className="h-10 w-full min-w-0 appearance-none rounded-lg border border-slate-200 bg-white py-2 pl-3 pr-9 text-xs text-slate-700 outline-none transition focus:border-customAccentBlue focus:ring-2 focus:ring-customAccentBlue/10 dark:border-customBorder dark:bg-custom_dark_primary dark:text-customHeading dark:[color-scheme:dark] [&::-webkit-calendar-picker-indicator]:absolute [&::-webkit-calendar-picker-indicator]:inset-y-0 [&::-webkit-calendar-picker-indicator]:right-0 [&::-webkit-calendar-picker-indicator]:h-full [&::-webkit-calendar-picker-indicator]:w-9 [&::-webkit-calendar-picker-indicator]:cursor-pointer [&::-webkit-calendar-picker-indicator]:opacity-0"
    />
    <CalendarDaysIcon
      aria-hidden="true"
      className="pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-customAccentBlue dark:text-customAccent"
    />
  </label>
);

export default function FilterBar({
  dateFilters,
  users,
  onDateChange,
  onApply,
  onReset,
  disabled,
  filters,
  onChange,
}) {
  return (
    <form
      onSubmit={onApply}
      className="rounded-xl border border-slate-200 bg-white p-3 shadow-sm dark:border-customBorder dark:bg-custom_dark_primary2"
    >
      <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-12 xl:items-end">
        <label className="text-xs font-semibold text-customInk dark:text-customHeading xl:col-span-2">
          Voucher ID
          <input
            className={fieldClass}
            value={filters.voucherId}
            onChange={(event) => onChange("voucherId", event.target.value)}
            placeholder="e.g. VCH-55877291"
          />
        </label>

        <div className="xl:col-span-2">
          <SelectField
            label="User"
            value={filters.user}
            onChange={(event) => onChange("user", event.target.value)}
          >
            <option value="">All Users</option>
            {users.map((user) => (
              <option key={user} value={user}>
                {user}
              </option>
            ))}
          </SelectField>
        </div>

        <div className="xl:col-span-1">
          <SelectField
            label="Type"
            value={filters.type}
            onChange={(event) => onChange("type", event.target.value)}
          >
            <option value="">All Types</option>
            <option>Single</option>
            <option>Mix</option>
          </SelectField>
        </div>

        <div className="xl:col-span-1">
          <SelectField
            label="Status"
            value={filters.status}
            onChange={(event) => onChange("status", event.target.value)}
          >
            <option value="">All Statuses</option>
            <option>Finished</option>
            <option>Pending</option>
            {/* <option>Open</option> */}
            <option>Recalculated</option>
            {/* <option>Refunded</option> */}
            <option>Rejected</option>
          </SelectField>
        </div>

        <div className="sm:col-span-2 xl:col-span-4">
          <span className="text-xs font-semibold text-customInk dark:text-customHeading">
            Date Range
          </span>
          <div className="mt-1 grid grid-cols-[minmax(0,1fr)_auto_minmax(0,1fr)] items-center gap-2">
            <DateField
              label="Start date"
              value={dateFilters.from}
              onChange={(event) => onDateChange("from", event.target.value)}
            />
            <span
              aria-hidden="true"
              className="text-sm text-slate-400 dark:text-customMuted"
            >
              &rarr;
            </span>
            <DateField
              label="End date"
              value={dateFilters.to}
              onChange={(event) => onDateChange("to", event.target.value)}
            />
          </div>
        </div>

        <div className="flex gap-2 sm:justify-end xl:col-span-2">
          <button
            type="button"
            disabled={disabled}
            onClick={onReset}
            className="flex h-10 flex-1 items-center justify-center gap-2 rounded-lg border border-blue-200 px-3 text-sm font-semibold text-customAccentBlue transition hover:bg-blue-50 disabled:cursor-not-allowed disabled:opacity-50 dark:border-customBorder dark:hover:bg-custom_dark_primary xl:flex-none"
          >
            <ArrowPathIcon className="h-4 w-4" /> Reset
          </button>
          <button
            type="submit"
            disabled={disabled}
            className="flex h-10 flex-1 items-center justify-center gap-2 rounded-lg bg-customAccentBlue px-4 text-sm font-semibold text-white shadow-sm transition hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-60 dark:bg-customAccent xl:flex-none"
          >
            <MagnifyingGlassIcon className="h-4 w-4" />
            {disabled ? "Loading…" : "Apply"}
          </button>
        </div>
      </div>
    </form>
  );
}
